import React from 'react';
import { Box, Link, Typography, Tooltip } from '@mui/material';
import { useBackendStatus } from '../../hooks/useBackendStatus';

/**
 * Formatiert Sekunden in ein lesbares Format: "Xd Xh Xm"
 */
function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);

  return parts.join(' ');
}

/**
 * Wiederverwendbare Footer-Komponente mit Frontend-Version und Backend-Status.
 *
 * Zeigt:
 * - © Jahr Welti-Furrer AG | pkon
 * - FE: Build-Hash (Tooltip mit Build-Details)
 * - 🟢/🔴 BE: Version-Hash (Tooltip mit Backend-Details)
 */
export const Footer: React.FC = () => {
  const backendStatus = useBackendStatus();

  const backendTooltip = backendStatus.isOnline
    ? [
        backendStatus.version && `Backend: ${backendStatus.version}`,
        backendStatus.environment && `Umgebung: ${backendStatus.environment}`,
        backendStatus.uptimeSeconds != null && `Uptime: ${formatUptime(backendStatus.uptimeSeconds)}`,
        backendStatus.buildTime && `Build: ${new Date(backendStatus.buildTime).toLocaleString('de-CH')}`,
      ]
        .filter(Boolean)
        .join(' | ')
    : 'Backend nicht erreichbar';

  return (
    <Box
      component="footer"
      sx={{
        mt: 4,
        py: 2,
        textAlign: 'center',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}
      >
        © {new Date().getFullYear()} Welti-Furrer AG | pkon
        {' · '}
        <Tooltip title={`Build: ${__BUILD_HASH__} | ${new Date(__BUILD_TIME__).toLocaleString('de-CH')}`}>
          <Typography
            component="span"
            variant="caption"
            color="text.disabled"
            sx={{ fontFamily: 'monospace', cursor: 'help' }}
          >
            FE: {__BUILD_HASH__}
          </Typography>
        </Tooltip>
        {' · '}
        <Tooltip title={backendTooltip}>
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}
          >
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontSize: '8px',
                lineHeight: 1,
                color: backendStatus.isOnline ? 'success.main' : 'error.main',
              }}
            >
              ●
            </Typography>
            <Typography
              component="span"
              variant="caption"
              color="text.disabled"
              sx={{ fontFamily: 'monospace' }}
            >
              BE: {backendStatus.isOnline && backendStatus.version ? backendStatus.version : '—'}
            </Typography>
          </Box>
        </Tooltip>
        {' · '}
        <Link
          href="https://7fg5g4bf.status.cron-job.org/"
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          color="text.disabled"
          underline="hover"
        >
          Cron-Job Status
        </Link>
      </Typography>
    </Box>
  );
};

export default Footer;
